import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faCheckDouble, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { notificationService, type NotificationItem } from '@/services/notification.service';
import { useAuthStore } from '@/stores/auth.store';
import { useHasPermission } from '@/hooks/use-permissions';
import { resolveNotificationDestination } from './notification-destination';

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/**
 * B6.17 — the Notification Center.
 *
 * Before this phase, notifications were written and never read: 15 worker
 * handlers created rows, no controller existed, and two rows had been sitting
 * PENDING with nothing in the system able to transition them.
 *
 * ── NO POLLING ──────────────────────────────────────────────────────────────
 * There is no `refetchInterval` here. The badge and list refresh because
 * `useRealtime` invalidates `['notifications-unread-count']` and
 * `['notifications']` when a signal arrives. The queries below exist to fetch
 * on mount and on demand — the push is what keeps them current.
 *
 * ── CLICKING A NOTIFICATION ─────────────────────────────────────────────────
 * Originally a click did one thing: mark read. The row named a job or a case and
 * left the user to go and find it, which made this an activity log rather than
 * an operational surface. A row now also navigates to the entity the
 * notification is actually about, resolved by
 * `resolveNotificationDestination()` from the notification's own event payload
 * and the caller's role and permissions — never from its title text.
 *
 * Read semantics are UNCHANGED and deliberately so: a click still marks a
 * PENDING row read via the same `PATCH /notifications/:id/read` mutation, and
 * navigation is added alongside it rather than replacing it. Nothing marks a row
 * read that a click did not already mark read before.
 *
 * A notification with no honest destination (`null`) keeps exactly its previous
 * behaviour — mark read if pending — and renders with no navigation affordance.
 * When there is nothing left to do at all (no destination and already read) the
 * row is a plain element rather than a button, so assistive technology is not
 * offered a control that does nothing.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const roleSlug = useAuthStore((s) => s.user?.roleSlug);
  const can = useHasPermission();

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: () => notificationService.unreadCount(),
    staleTime: 30_000,
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationService.list({ limit: 15 }),
    // Only fetch the list once the panel has actually been opened — the badge
    // alone does not need it.
    enabled: open,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    void queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
  };

  const markRead = useMutation({
    mutationFn: (id: string) => notificationService.markRead(id),
    onSuccess: invalidate,
  });

  const markAllRead = useMutation({
    mutationFn: () => notificationService.markAllRead(),
    onSuccess: invalidate,
  });

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  const items: NotificationItem[] = data?.items ?? [];

  /**
   * Mark read (unchanged), then go. Closing the panel first matches the existing
   * dismissal convention — Escape and click-outside both close it — and leaving
   * a dropdown floating over a page the user just navigated to would be the odd
   * behaviour. `navigate` is client-side, so no reload and the router's history
   * is preserved; the mark-read mutation completes independently and its
   * `onSuccess` invalidation is safe after the panel has closed.
   */
  const handleActivate = (n: NotificationItem, destination: string | null) => {
    if (n.status === 'PENDING') markRead.mutate(n.id);
    if (destination) {
      setOpen(false);
      navigate(destination);
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-primary/[0.07] hover:text-foreground"
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <FontAwesomeIcon icon={faBell} className="h-4 w-4" />
        {unreadCount > 0 && (
          <span
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground"
            data-testid="notification-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-[22rem] overflow-hidden rounded-xl border border-border bg-card shadow-lg">
          <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
            <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faCheckDouble} className="h-3 w-3" />
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="space-y-2 p-4">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="h-10 animate-pulse rounded bg-muted" />
                ))}
              </div>
            ) : isError ? (
              <p className="px-4 py-8 text-center text-sm text-destructive">
                Could not load notifications.
              </p>
            ) : items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                You&apos;re all caught up.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {items.map((n) => {
                  const destination = resolveNotificationDestination(n, { roleSlug, can });
                  // A row is a control when a click still does something: open
                  // the entity, or mark an unread row read. When neither
                  // applies it is inert content, and rendering it as a button
                  // would put a do-nothing control in the tab order.
                  const isInteractive = destination !== null || n.status === 'PENDING';

                  const body = (
                    <>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-medium text-foreground">{n.title}</p>
                          <span className="shrink-0 text-[11px] text-muted-foreground">
                            {relativeTime(n.createdAt)}
                          </span>
                        </div>
                        <p className="mt-0.5 text-xs text-muted-foreground">{n.message}</p>
                      </div>
                      {destination && (
                        // The same affordance the Request Center uses on its own
                        // navigating rows. Decorative only — the accessible name
                        // is the title and message the button already contains.
                        <FontAwesomeIcon
                          icon={faChevronRight}
                          aria-hidden="true"
                          className="mt-0.5 h-3 w-3 shrink-0 self-center text-muted-foreground"
                        />
                      )}
                    </>
                  );

                  return (
                    <li
                      key={n.id}
                      className={n.status === 'PENDING' ? 'bg-primary/[0.04]' : undefined}
                    >
                      {isInteractive ? (
                        <button
                          type="button"
                          data-testid={`notification-item-${n.id}`}
                          onClick={() => handleActivate(n, destination)}
                          className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${
                            destination ? 'cursor-pointer' : ''
                          }`}
                        >
                          {body}
                        </button>
                      ) : (
                        <div
                          data-testid={`notification-item-${n.id}`}
                          className="flex w-full items-start gap-3 px-4 py-3 text-left"
                        >
                          {body}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
